import _ from "lodash";
import PropTypes from "prop-types";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { SelectedStoreContext } from "../../../../../../../../../../components/SelectedStoreProvider";
import DEFAULT_TIMEZONE from "../../../../../../../../../../utils/date/DEFAULT_TIMEZONE";
import { useContext } from "react";
import useStore from "../../../../../../../../../../utils/api/data-hooks/useStore";
import { useAuthContext } from "@rokasaitenta/react-auth-provider";

import useBatch from "../../../../../../../../../../utils/api/data-hooks/useBatch";
import useSoldGoods from "../../../../../../../../../../utils/api/data-hooks/useSoldGoods";
import CenteredCircularProgress from "../../../../../../../../../../components/CenteredCircularProgress";
import ErrorMessage from "../../../../../../../../../../components/ErrorMessage";

import formatMoney from "../../../../../../../../../../utils/currency/formatMoney";

import GoodRow from "./components/GoodRow";

const GoodTable = ({ goods, onDeleteGood }) => {
  const makeGoodDeleteHandler = (good) => () => onDeleteGood(good);

  const [{ token }] = useAuthContext();

  const { batchId } = useParams();

  const batchData = useBatch(batchId, token);

  const remainderGoods = batchData.data.remainderGoods || [];
  const remainderGoodsCount = remainderGoods.length || 0;
  const remainderGoodsTotalPrice = _.sumBy(remainderGoods, "price") || 0;

  // Group remainder goods by category and calculate counts and sums
  const remainderGoodsByCategory = _.groupBy(remainderGoods, "category");

  // Convert to array for easier rendering
  const remainderGoodsCategorySummaryArray = _.map(
    remainderGoodsByCategory,
    (items, category) => ({
      category: category || "Uncategorized",
      count: items.length,
      totalPrice: _.sumBy(items, "price") || 0,
    })
  );

  // Sort by count (descending)
  const sortedRemainderGoodsCategorySummary = _.orderBy(
    remainderGoodsCategorySummaryArray,
    ["count", "totalPrice"],
    ["desc", "desc"]
  );

  return (
    <Box>
      <Paper>
        <Toolbar sx={{ display: "flex", "& > *:not(:last-child)": { mr: 1 } }}>
          <Typography>
            Prekių:{" "}
            <Box component="span" fontWeight="medium">
              {goods.length}
            </Box>
          </Typography>
          <Typography>
            Vertė:{" "}
            <Box component="span" fontWeight="medium">
              {formatMoney(_.sumBy(goods, "price"))}
            </Box>
          </Typography>
        </Toolbar>

        {/* New table for returned goods by category */}
        <Paper sx={{ mt: 2, mb: 2 }}>
          <Toolbar>
            <Typography fontWeight="medium">
              Grąžintos prekės pagal kategoriją
            </Typography>
          </Toolbar>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kategorija</TableCell>
                  <TableCell align="right">Vnt.</TableCell>
                  <TableCell align="right">Suma (EUR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRemainderGoodsCategorySummary.map((item) => (
                  <TableRow key={item.category}>
                    <TableCell>{item.category}</TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                    <TableCell align="right">
                      {formatMoney(item.totalPrice, false)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="medium">Iš viso</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {remainderGoodsCount}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {formatMoney(remainderGoodsTotalPrice, false)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Original goods table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">Kaina</TableCell>
                <TableCell>Pridėjimo laikas</TableCell>
                <TableCell>Kategorija</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {_.orderBy(goods, ["created_at"], ["desc"]).map((good) => (
                <GoodRow
                  key={good.id}
                  data={good}
                  onDelete={makeGoodDeleteHandler(good)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

GoodTable.propTypes = {
  goods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      created_at: PropTypes.string.isRequired,
    })
  ).isRequired,
  onDeleteGood: PropTypes.func.isRequired,
};

export default GoodTable;
